import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Phone,
  PhoneOff,
  Delete,
  Settings,
  Clock,
  FileText,
  Play,
  ChevronDown,
  ChevronUp,
  Loader2,
  X
} from 'lucide-react';
import { dialerAPI, callsAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import webSocketService from '../services/websocket';
import toast from 'react-hot-toast';

const KEYPAD_KEYS = [
  ['1', '2', '3'],
  ['4', '5', '6'],
  ['7', '8', '9'],
  ['*', '0', '#']
];

const STATUS_LABELS = {
  idle: 'Ready',
  initiating: 'Initiating...',
  calling_user: 'Calling you...',
  connected: 'Connected',
  ended: 'Call Ended'
};

const STATUS_COLORS = {
  idle: 'text-gray-500',
  initiating: 'text-yellow-600',
  calling_user: 'text-blue-600',
  connected: 'text-green-600',
  ended: 'text-gray-500'
};

const DialerPage = () => {
  const { user } = useAuth();
  const [number, setNumber] = useState('');
  const [contactName, setContactName] = useState('');
  const [callStatus, setCallStatus] = useState('idle');
  const [activeCallId, setActiveCallId] = useState(null);
  const [activeVonageCallId, setActiveVonageCallId] = useState(null);
  const [callDuration, setCallDuration] = useState(0);
  const [callHistory, setCallHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [phoneNumbers, setPhoneNumbers] = useState([]);
  const [selectedFromNumberId, setSelectedFromNumberId] = useState('');
  const [showSettings, setShowSettings] = useState(false);
  const [callbackNumber, setCallbackNumber] = useState(
    () => localStorage.getItem('dialer_callback_number') || ''
  );
  const [transcribing, setTranscribing] = useState(null);
  const [playingAudio, setPlayingAudio] = useState(null);

  const durationTimerRef = useRef(null);
  const audioRef = useRef(null);

  // Load phone numbers and call history on mount
  useEffect(() => {
    loadPhoneNumbers();
    loadHistory();
  }, []);

  // Listen for WebSocket dialer status updates
  useEffect(() => {
    const handleDialerStatus = (data) => {
      if (data.callId === activeCallId || data.vonageCallId === activeVonageCallId) {
        setCallStatus(data.status);

        if (data.status === 'connected') {
          startDurationTimer();
        }

        if (data.status === 'ended') {
          stopDurationTimer();
          // Refresh history after call ends
          setTimeout(() => loadHistory(), 2000);
        }
      }
    };

    webSocketService.on('dialer_status', handleDialerStatus);
    return () => webSocketService.off('dialer_status', handleDialerStatus);
  }, [activeCallId, activeVonageCallId]);

  const loadPhoneNumbers = async () => {
    try {
      const response = await callsAPI.getPhoneNumbers();
      const numbers = response.data?.data?.numbers || response.data?.numbers || [];
      setPhoneNumbers(numbers);
      if (numbers.length > 0 && !selectedFromNumberId) {
        setSelectedFromNumberId(numbers[0].id);
      }
    } catch (error) {
      console.error('Failed to load phone numbers:', error);
    }
  };

  const loadHistory = async () => {
    try {
      setHistoryLoading(true);
      const response = await dialerAPI.getHistory();
      setCallHistory(response.data?.calls || []);
    } catch (error) {
      console.error('Failed to load history:', error);
    } finally {
      setHistoryLoading(false);
    }
  };

  const startDurationTimer = () => {
    setCallDuration(0);
    durationTimerRef.current = setInterval(() => {
      setCallDuration(prev => prev + 1);
    }, 1000);
  };

  const stopDurationTimer = () => {
    if (durationTimerRef.current) {
      clearInterval(durationTimerRef.current);
      durationTimerRef.current = null;
    }
  };

  const formatDuration = (seconds) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  const formatPhoneDisplay = (num) => {
    const digits = num.replace(/\D/g, '');
    if (digits.length === 11 && digits.startsWith('1')) {
      return `+1 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
    }
    if (digits.length === 10) {
      return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
    }
    return num;
  };

  const handleKeyPress = (key) => {
    setNumber(prev => prev + key);
  };

  const handleBackspace = () => {
    setNumber(prev => prev.slice(0, -1));
  };

  const handleCall = async () => {
    if (!number) {
      toast.error('Enter a phone number');
      return;
    }
    if (!callbackNumber) {
      toast.error('Set your callback number in settings first');
      setShowSettings(true);
      return;
    }

    try {
      setCallStatus('initiating');
      const response = await dialerAPI.initiateCall({
        targetNumber: number,
        callbackNumber,
        fromNumberId: selectedFromNumberId || undefined,
        contactName: contactName || undefined
      });

      if (response.data.success) {
        setActiveCallId(response.data.callId);
        setActiveVonageCallId(response.data.vonageCallId);
        setCallStatus('calling_user');
        toast.success('Calling your phone...');
      } else {
        setCallStatus('idle');
        toast.error(response.data.error || 'Failed to initiate call');
      }
    } catch (error) {
      setCallStatus('idle');
      toast.error(error.response?.data?.error || 'Failed to initiate call');
    }
  };

  const handleHangup = async () => {
    if (!activeVonageCallId) return;

    try {
      await dialerAPI.hangup({ vonageCallId: activeVonageCallId });
      setCallStatus('ended');
      stopDurationTimer();
      toast('Call ended');
      setTimeout(() => {
        setCallStatus('idle');
        setActiveCallId(null);
        setActiveVonageCallId(null);
        setCallDuration(0);
        loadHistory();
      }, 2000);
    } catch (error) {
      toast.error('Failed to end call');
    }
  };

  const handleTranscribe = async (callId) => {
    setTranscribing(callId);
    try {
      const response = await dialerAPI.transcribe(callId);
      if (response.data.success) {
        toast.success(response.data.cached ? 'Transcript loaded' : 'Transcription complete');
        loadHistory();
      }
    } catch (error) {
      toast.error(error.response?.data?.error || 'Transcription failed');
    } finally {
      setTranscribing(null);
    }
  };

  const handlePlayRecording = (recordingUrl) => {
    if (playingAudio === recordingUrl) {
      audioRef.current?.pause();
      setPlayingAudio(null);
      return;
    }

    if (audioRef.current) {
      audioRef.current.pause();
    }

    // Vonage recordings require auth - proxy through our API if needed
    const audio = new Audio(recordingUrl);
    audioRef.current = audio;
    setPlayingAudio(recordingUrl);

    audio.onended = () => setPlayingAudio(null);
    audio.onerror = () => {
      toast.error('Failed to play recording');
      setPlayingAudio(null);
    };
    audio.play();
  };

  const saveCallbackNumber = () => {
    localStorage.setItem('dialer_callback_number', callbackNumber);
    toast.success('Callback number saved');
    setShowSettings(false);
  };

  const isInCall = ['initiating', 'calling_user', 'connected'].includes(callStatus);

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-gray-900">Manual Dialer</h2>
        <button
          onClick={() => setShowSettings(!showSettings)}
          className="flex items-center gap-2 px-3 py-2 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg"
        >
          <Settings className="w-4 h-4" />
          Settings
        </button>
      </div>

      {/* Settings Panel */}
      {showSettings && (
        <div className="bg-white rounded-lg shadow p-4 border border-gray-200">
          <h3 className="text-sm font-medium text-gray-900 mb-3">Dialer Settings</h3>
          <div className="space-y-3">
            <div>
              <label className="block text-sm text-gray-600 mb-1">
                Your phone number (we call this number first, then bridge to the target)
              </label>
              <div className="flex gap-2">
                <input
                  type="tel"
                  value={callbackNumber}
                  onChange={(e) => setCallbackNumber(e.target.value)}
                  placeholder="+1234567890"
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                <button
                  onClick={saveCallbackNumber}
                  className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700"
                >
                  Save
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Keypad */}
        <div className="bg-white rounded-lg shadow p-6">
          {/* From number selector */}
          <div className="mb-4">
            <label className="block text-xs text-gray-500 mb-1">From Number</label>
            <select
              value={selectedFromNumberId}
              onChange={(e) => setSelectedFromNumberId(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
              disabled={isInCall}
            >
              {phoneNumbers.map((pn) => (
                <option key={pn.id} value={pn.id}>
                  {pn.phoneNumber} {pn.friendlyName ? `(${pn.friendlyName})` : ''}
                </option>
              ))}
              {phoneNumbers.length === 0 && (
                <option value="">No numbers available</option>
              )}
            </select>
          </div>

          {/* Contact name (optional) */}
          <div className="mb-4">
            <input
              type="text"
              value={contactName}
              onChange={(e) => setContactName(e.target.value)}
              placeholder="Contact name (optional)"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
              disabled={isInCall}
            />
          </div>

          {/* Number display */}
          <div className="text-center mb-4">
            <div className="text-2xl font-mono tracking-wider min-h-[2.5rem] flex items-center justify-center text-gray-900">
              {number ? formatPhoneDisplay(number) : (
                <span className="text-gray-400">Enter number</span>
              )}
            </div>
          </div>

          {/* Call status */}
          <div className="text-center mb-4">
            <span className={`text-sm font-medium ${STATUS_COLORS[callStatus]}`}>
              {STATUS_LABELS[callStatus]}
            </span>
            {callStatus === 'connected' && (
              <span className="ml-2 text-sm text-gray-500 font-mono">
                {formatDuration(callDuration)}
              </span>
            )}
          </div>

          {/* Keypad grid */}
          <div className="grid grid-cols-3 gap-2 mb-4 max-w-xs mx-auto">
            {KEYPAD_KEYS.flat().map((key) => (
              <button
                key={key}
                onClick={() => handleKeyPress(key)}
                disabled={isInCall}
                className="h-14 text-xl font-medium rounded-lg bg-gray-50 hover:bg-gray-100 active:bg-gray-200 text-gray-900 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {key}
              </button>
            ))}
          </div>

          {/* Action buttons */}
          <div className="flex items-center justify-center gap-4">
            <button
              onClick={handleBackspace}
              disabled={isInCall || !number}
              className="p-3 rounded-full text-gray-500 hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed"
              title="Backspace"
            >
              <Delete className="w-5 h-5" />
            </button>

            {!isInCall ? (
              <button
                onClick={handleCall}
                disabled={!number}
                className="p-4 rounded-full bg-green-500 hover:bg-green-600 text-white shadow-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                title="Call"
              >
                <Phone className="w-6 h-6" />
              </button>
            ) : (
              <button
                onClick={handleHangup}
                className="p-4 rounded-full bg-red-500 hover:bg-red-600 text-white shadow-lg transition-colors"
                title="Hang up"
              >
                <PhoneOff className="w-6 h-6" />
              </button>
            )}

            <button
              onClick={() => { setNumber(''); setContactName(''); }}
              disabled={isInCall}
              className="p-3 rounded-full text-gray-500 hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed"
              title="Clear"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Recent calls */}
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
            <h3 className="text-sm font-medium text-gray-900">Recent Calls</h3>
            <button
              onClick={loadHistory}
              className="text-xs text-gray-500 hover:text-gray-700"
            >
              Refresh
            </button>
          </div>

          <div className="divide-y divide-gray-100 max-h-[500px] overflow-y-auto">
            {historyLoading ? (
              <div className="p-6 text-center text-gray-500">
                <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" />
                Loading...
              </div>
            ) : callHistory.length === 0 ? (
              <div className="p-6 text-center text-gray-500 text-sm">
                No calls yet. Dial a number to get started.
              </div>
            ) : (
              callHistory.map((call) => (
                <div key={call.id} className="px-4 py-3 hover:bg-gray-50">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-gray-900 truncate">
                          {call.contactName || formatPhoneDisplay(call.toNumber || '')}
                        </span>
                        <span className={`text-xs px-1.5 py-0.5 rounded ${
                          call.status === 'completed' ? 'bg-green-100 text-green-700' :
                          call.status === 'failed' ? 'bg-red-100 text-red-700' :
                          'bg-gray-100 text-gray-600'
                        }`}>
                          {call.status}
                        </span>
                      </div>
                      {call.contactName && (
                        <div className="text-xs text-gray-500">{formatPhoneDisplay(call.toNumber || '')}</div>
                      )}
                      <div className="flex items-center gap-3 mt-1 text-xs text-gray-400">
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {new Date(call.startedAt).toLocaleString()}
                        </span>
                        {call.durationSeconds && (
                          <span>{formatDuration(call.durationSeconds)}</span>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center gap-1 ml-2">
                      {call.recordingUrl && (
                        <button
                          onClick={() => handlePlayRecording(call.recordingUrl)}
                          className="p-1.5 rounded hover:bg-gray-200 text-gray-500"
                          title="Play recording"
                        >
                          <Play className={`w-4 h-4 ${playingAudio === call.recordingUrl ? 'text-blue-600' : ''}`} />
                        </button>
                      )}
                      {call.recordingUrl && !call.transcript && (
                        <button
                          onClick={() => handleTranscribe(call.id)}
                          disabled={transcribing === call.id}
                          className="p-1.5 rounded hover:bg-gray-200 text-gray-500 disabled:opacity-50"
                          title="Transcribe"
                        >
                          {transcribing === call.id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <FileText className="w-4 h-4" />
                          )}
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Transcript display */}
                  {call.transcript && (
                    <div className="mt-2 p-2 bg-gray-50 rounded text-xs text-gray-700 whitespace-pre-wrap max-h-32 overflow-y-auto">
                      {call.transcript}
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DialerPage;
